import { Component, AfterViewInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { GridOptions } from "ag-grid-community";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { DeviationService } from "src/app/shared/services/deviation.service";
import { JsignalService } from "src/app/shared/services/jsignal.service";

@Component({
    selector: "app-detail-row-renderer",
    templateUrl: "./detail-row-renderer.component.html",
    styleUrls: ["./detail-row-renderer.component.scss"],
    standalone: false
})
export class DetailRowRendererComponent implements ICellRendererAngularComp, AfterViewInit {
  devid!: number;
  metaEntries: any[] = [];
  devEntries: any[] = [];
  rootCauses: any[] = [];
  capas: any[] = [];
  metaLoaded = false;
  devLoaded = false;
  loadRootCause = false;
  loadCapa = false;
  params: any;
  rootcausecolumnDefs: any[];
  capacolumnDefs: any[];
  defaultColDef: {};
  gridOptions: GridOptions = {};
  gridApi: any;

  constructor(
    private snackbar: MatSnackBar,
    private spinner: NgxUiLoaderService,
    private jsignalsService: JsignalService,
    private deviationService: DeviationService,
  ) {}

  agInit(params: any): void {
    this.params = params;
    this.devid = params.data?.devid;
    this.loadMetadata();
  }

  ngAfterViewInit(): void {
    this.resetRowHeight();
  }

  refresh(): boolean {
    return false;
  }

  onTabChange(event: MatTabChangeEvent): void {
    switch (event.index) {
      case 0:
        if (!this.metaLoaded) this.loadMetadata();
        break;
      case 1:
        if (!this.devLoaded) this.loadDeviationDetails();
        break;
      case 2:
        if (!this.loadRootCause) this.loadRootCauseData();
        break;
      case 3:
        if (!this.loadCapa) this.loadCapaData();
        break;
    }
    this.resetRowHeight();
  }

  // ================= DATA LOADING METHODS =================
  private loadMetadata(): void {
    this.metaLoaded = true;
    this.spinner.start();
    
    this.deviationService.getDeviationMetaData(this.devid).subscribe({
      next: (res: any) => {
        this.metaEntries = this.mapObjectToArray(res || {});
        this.spinner.stop();
        this.resetRowHeight();
      },
      error: () => {
        this.spinner.stop();
        this.showError("Failed to load metadata");
      }
    });
  }

  private loadDeviationDetails(): void {
    this.devLoaded = true;
    this.spinner.start();
    
    this.deviationService.getDeviationDetails(this.devid).subscribe({
      next: (res: any) => {
        this.devEntries = this.mapObjectToArray(res || {});
        this.spinner.stop();
        this.resetRowHeight();
      },
      error: () => {
        this.spinner.stop();
        this.showError("Failed to load deviation details");
      }
    });
  }

  private loadRootCauseData(): void {
    this.loadRootCause = true;
    this.initializeRootCauseGrid();
    this.spinner.start();
    
    this.deviationService.getRootCauses(this.devid).subscribe({
      next: (res: any) => {
        this.rootCauses = res || [];
        this.spinner.stop();
        this.resetRowHeight();
      },
      error: () => {
        this.spinner.stop();
        this.showError("Failed to load root causes");
      }
    });
  }

  private loadCapaData(): void {
    this.loadCapa = true;
    this.initializeCapaGrid();
    this.spinner.start();
    
    this.deviationService.getCapas(this.devid).subscribe({
      next: (res: any) => {
        this.capas = res || [];
        this.spinner.stop();
        this.resetRowHeight();
      },
      error: () => {
        this.spinner.stop();
        this.showError("Failed to load CAPA data");
      }
    });
  }

  // ================= GRID INITIALIZATION =================
  private initializeRootCauseGrid(): void {
    this.rootcausecolumnDefs = [
      { headerName: "Name", field: "rootCauseName", ...this.jsignalsService.autoTooltip("Name") },
      { headerName: "Cause Type", field: "causeType", ...this.jsignalsService.autoTooltip("Cause Type") },
      { headerName: "Category", field: "category", ...this.jsignalsService.autoTooltip("Category") },
      { headerName: "Sub Category", field: "subCategory", ...this.jsignalsService.autoTooltip("Sub Category") },
      { headerName: "Analysis Type", field: "rootCauseAnalysisType", ...this.jsignalsService.autoTooltip("Analysis Type") },
      { headerName: "Description", field: "rootCauseDescription", ...this.jsignalsService.autoTooltip("Description") },
      { headerName: "Analysis", field: "rootCauseAnalysis", ...this.jsignalsService.autoTooltip("Analysis") },
    ];
    this.initializeGridCommonConfig();
  }

  private initializeCapaGrid(): void {
    this.capacolumnDefs = [
      { headerName: "Name", field: "capaName", ...this.jsignalsService.autoTooltip("Name"), flex: 1 },
      { headerName: "Action Type", field: "actionType", ...this.jsignalsService.autoTooltip("Action Type"), flex: 1 },
      { headerName: "Due Date", field: "dueDate", ...this.jsignalsService.autoTooltip("Due Date"), flex: 1 },
      { headerName: "Description", field: "capaDescription", ...this.jsignalsService.autoTooltip("Description"), flex: 2 },
    ];
    this.initializeGridCommonConfig();
  }

  private initializeGridCommonConfig(): void {
    this.defaultColDef = {
filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      },
      sortable: true,
      filter: true,
      resizable: true,
    };
    this.gridOptions = {
      rowHeight: 40,
    };
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
  }

  // ================= UTILITY METHODS =================
  private mapObjectToArray(obj: any): any[] {
    return Object.keys(obj).map((key) => ({
      label: this.formatKey(key),
      value: obj[key],
    }));
  }

  private formatKey(key: string): string {
    return key
      .replace(/^.*\./, "")
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
  }

  private resetRowHeight(): void {
    setTimeout(() => {
      if (this.params && this.params.api) {
        this.params.api.resetRowHeights();
      }
    });
  }

  private showError(message: string): void {
    this.snackbar.open(message, "Close", { duration: 5000 });
  }
}